import { useState } from 'react'
import { Link, createFileRoute } from '@tanstack/react-router'
import { ArrowLeft, GitBranch, History, Layers } from 'lucide-react'

import type { ProductLotHistoryEntry } from '@/lib/api/product-lot-api'
import { PageBackButton } from '@/components/common/PageBackButton'
import { HistoryEntryCard } from '@/components/common/HistoryEntryCard'
import { LotLineageTimeline } from '@/components/orders/lot-lineage-timeline'
import { ProductLotHistoryTable } from '@/components/product-lots/ProductLotHistoryTable'
import { ProductLotStatusBadge } from '@/components/product-lots/ProductLotStatusBadge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { PositionPicker } from '@/components/warehouse-positions/position-picker'
import {
    useProductLotHistoryQuery,
    useProductLotQuery,
} from '@/hooks/use-product-lot-query'
import {
    useUpdateLotDiscount,
    useUpdateLotPosition,
} from '@/hooks/use-product-lot-mutation'
import { formatDateTimeVN, formatQty } from '@/lib/format'
import { m } from '@/lib/paraglide/messages'

// LotDiscountEditor — hiển thị + sửa mức CK đánh dấu của lô (ưu tiên chọn lô khi
// đơn có CK khớp). Lưu qua PATCH /product-lots/:id/discount.
function LotDiscountEditor({
    lotId,
    current,
}: {
    lotId: string
    current: number
}) {
    const [value, setValue] = useState(String(current))
    const update = useUpdateLotDiscount()
    const parsed = Number(value)
    const dirty = Number.isFinite(parsed) && parsed !== current
    const valid = Number.isFinite(parsed) && parsed >= 0 && parsed <= 100
    return (
        <div className="flex items-center gap-2">
            <Input
                type="number"
                min={0}
                max={100}
                step="0.01"
                value={value}
                onChange={(e) => setValue(e.target.value)}
                className="w-24"
            />
            <Button
                type="button"
                size="sm"
                variant="outline"
                disabled={!dirty || !valid || update.isPending}
                onClick={() =>
                    update.mutate({ id: lotId, discountPercent: parsed })
                }
            >
                {m.common_save()}
            </Button>
        </div>
    )
}

// LotPositionEditor — hiển thị + sửa vị trí kho của lô. Lưu qua
// PATCH /product-lots/:id/position.
function LotPositionEditor({
    lotId,
    current,
    currentLabel,
}: {
    lotId: string
    current: string | null | undefined
    currentLabel: string
}) {
    const [value, setValue] = useState(current ?? undefined)
    const update = useUpdateLotPosition()
    const dirty = !!value && value !== (current ?? undefined)
    return (
        <div className="space-y-1">
            {/* Vị trí hiện tại dạng text — picker chỉ tải 50 vị trí active nên có
                thể không hiển thị lựa chọn hiện tại; text này luôn cho thấy đúng. */}
            <p className="text-base font-medium md:text-sm">{currentLabel}</p>
            <div className="flex items-center gap-2">
                <PositionPicker
                    lotType="product"
                    value={value}
                    onChange={setValue}
                    fallbackLabel={currentLabel}
                />
                <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    disabled={!dirty || update.isPending}
                    onClick={() =>
                        value && update.mutate({ id: lotId, positionId: value })
                    }
                >
                    {m.common_save()}
                </Button>
            </div>
        </div>
    )
}

const HISTORY_REASON_LABEL: Record<string, () => string> = {
    from_purchase: () => m.product_lot_history_reason_from_purchase(),
    adjustment: () => m.product_lot_history_reason_adjustment(),
    for_sale: () => m.product_lot_history_reason_for_sale(),
    discard: () => m.product_lot_history_reason_discard(),
    from_packaging: () => m.pll_in_reason_from_packaging(),
    from_customer_return: () => m.pll_in_reason_from_customer_return(),
    from_order_cancel: () => m.pll_in_reason_from_order_cancel(),
    from_manufacturing_partner: () =>
        m.pll_in_reason_from_manufacturing_partner(),
    from_tea_sifting: () => m.pll_in_reason_from_tea_sifting(),
    from_agent_return: () => m.pll_in_reason_from_agent_return(),
    for_order: () => m.pkl_out_reason_for_sale(),
}

function reasonLabel(reason: string): string {
    const fn = HISTORY_REASON_LABEL[reason]
    return fn ? fn() : reason
}

function NoteLink({ entry }: { entry: ProductLotHistoryEntry }) {
    const className = 'font-mono text-sm text-primary hover:underline'
    if (entry.note_type === 'in') {
        return (
            <Link
                to="/sales-warehouse/stock-in-notes/$id"
                params={{ id: entry.note_id }}
                className={className}
            >
                {entry.note_code}
            </Link>
        )
    }
    if (
        entry.note_type === 'transfer_in' ||
        entry.note_type === 'transfer_out'
    ) {
        return (
            <Link
                to="/sales-warehouse/transfer-notes/$id"
                params={{ id: entry.note_id }}
                className={className}
            >
                {entry.note_code}
            </Link>
        )
    }
    return (
        <Link
            to="/sales-warehouse/stock-out-notes/$id"
            params={{ id: entry.note_id }}
            className={className}
        >
            {entry.note_code}
        </Link>
    )
}

export const Route = createFileRoute(
    '/_app/_authed/_sidebar/sales-warehouse/lots/$id',
)({
    component: Page,
})

function Page() {
    const { id } = Route.useParams()
    const { data, isLoading } = useProductLotQuery(id)
    const historyQuery = useProductLotHistoryQuery(id)
    const [traceOpen, setTraceOpen] = useState(false)

    const lot = data?.data
    const history = historyQuery.data?.pages.flatMap((p) => p.data ?? []) ?? []
    const historyLoading = historyQuery.isLoading

    if (isLoading) return <div className="p-6">{m.sl_lots_loading()}</div>
    if (!lot)
        return <div className="p-6">{m.product_lot_detail_not_found()}</div>

    return (
        <div className="space-y-4 page-pad">
            <div className="flex flex-wrap items-center gap-3">
                <PageBackButton asChild>
                    <Link to="/sales-warehouse/lots">
                        <ArrowLeft className="size-4" />
                    </Link>
                </PageBackButton>
                <Layers className="size-5" />
                <h1 className="font-mono text-base font-semibold md:text-2xl">
                    {lot.code}
                </h1>
                <div className="ml-auto">
                    <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setTraceOpen(true)}
                    >
                        <GitBranch className="size-4 mr-1" />
                        {m.product_lot_detail_trace_button()}
                    </Button>
                </div>
            </div>

            <Dialog open={traceOpen} onOpenChange={setTraceOpen}>
                <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>
                            {m.product_lot_detail_trace_title()}
                        </DialogTitle>
                        <DialogDescription className="font-mono">
                            {lot.code}
                        </DialogDescription>
                    </DialogHeader>
                    <LotLineageTimeline lotId={lot.id} source="sales_lot" />
                </DialogContent>
            </Dialog>

            <div className="grid gap-4 md:grid-cols-2">
                <Card>
                    <CardHeader>
                        <CardTitle>
                            {m.product_lot_detail_info_title()}
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="grid gap-3 grid-cols-2">
                        <div className="col-span-2">
                            <Field
                                label={m.product_lot_detail_label_product()}
                                value={lot.product_name ?? '—'}
                            />
                        </div>
                        <div className="col-span-2">
                            <Field
                                label={m.product_lot_detail_label_warehouse()}
                                value={lot.warehouse_name ?? '—'}
                            />
                        </div>
                        <div className="col-span-2">
                            <p className="text-xs text-muted-foreground">
                                {m.lot_position_label()}
                            </p>
                            <div className="mt-0.5">
                                <LotPositionEditor
                                    lotId={lot.id}
                                    current={lot.position_id}
                                    currentLabel={
                                        lot.position_code
                                            ? lot.position_name
                                                ? `${lot.position_code} — ${lot.position_name}`
                                                : lot.position_code
                                            : m.warehouse_position_none()
                                    }
                                />
                            </div>
                        </div>
                        <Field
                            label={m.product_lot_detail_label_manufactured()}
                            value={lot.manufactured_date?.slice(0, 10) ?? '—'}
                        />
                        <Field
                            label={m.product_lot_detail_label_expiry()}
                            value={lot.expiry_date?.slice(0, 10) ?? '—'}
                        />
                        <div>
                            <p className="text-xs text-muted-foreground">
                                {m.product_lot_detail_label_discount()}
                            </p>
                            <div className="mt-0.5">
                                <LotDiscountEditor
                                    lotId={lot.id}
                                    current={lot.discount_percent ?? 0}
                                />
                            </div>
                        </div>
                        <Field
                            label={m.sl_lot_created_at()}
                            value={formatDateTimeVN(lot.created_at)}
                        />
                        {lot.created_by_name && (
                            <Field
                                label={m.sl_lot_created_by()}
                                value={lot.created_by_name}
                            />
                        )}
                        {lot.notes && (
                            <div className="col-span-2">
                                <Field
                                    label={m.sl_lot_notes()}
                                    value={lot.notes}
                                />
                            </div>
                        )}
                    </CardContent>
                </Card>

                <Card>
                    <CardHeader>
                        <CardTitle>
                            {m.product_lot_detail_stock_title()}
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="grid gap-3 grid-cols-1">
                        <div className="rounded-md bg-primary/10 p-3 text-center">
                            <p className="text-xs text-muted-foreground">
                                {m.product_lot_detail_label_available()}
                            </p>
                            <p className="text-2xl font-semibold tabular-nums text-primary md:text-xl">
                                {formatQty(lot.quantity_available)}
                            </p>
                        </div>
                    </CardContent>
                </Card>
            </div>

            <Card>
                <CardHeader>
                    <div className="flex items-center gap-2">
                        <History className="size-5" />
                        <CardTitle>
                            {m.product_lot_detail_history_title()}
                        </CardTitle>
                    </div>
                </CardHeader>
                <CardContent>
                    {historyLoading ? (
                        <p className="text-sm text-muted-foreground py-2">
                            {m.common_loading()}
                        </p>
                    ) : history.length === 0 ? (
                        <p className="text-sm text-muted-foreground py-2">
                            {m.product_lot_history_empty()}
                        </p>
                    ) : (
                        <>
                            {/* Mobile: card list */}
                            <div className="space-y-2 md:hidden">
                                {history.map((h) => (
                                    <HistoryEntryCard
                                        key={`${h.note_id}-${h.note_type}`}
                                        type={h.note_type}
                                        createdAt={h.created_at}
                                        noteLink={<NoteLink entry={h} />}
                                        statusBadge={
                                            <ProductLotStatusBadge
                                                status={h.status}
                                            />
                                        }
                                        reason={reasonLabel(h.reason)}
                                        notes={h.notes}
                                        quantity={h.quantity}
                                        createdBy={h.created_by_name}
                                        orderCode={h.order_code}
                                        internalOrderCode={
                                            h.internal_order_code
                                        }
                                    />
                                ))}
                            </div>

                            {/* Desktop: table */}
                            <div className="hidden md:block">
                                <ProductLotHistoryTable
                                    entries={history}
                                    isLoading={historyLoading}
                                />
                            </div>
                        </>
                    )}
                    {historyQuery.hasNextPage && (
                        <Button
                            variant="outline"
                            className="w-full h-11 mt-3"
                            disabled={historyQuery.isFetchingNextPage}
                            onClick={() => void historyQuery.fetchNextPage()}
                        >
                            {historyQuery.isFetchingNextPage
                                ? m.list_loading_more()
                                : m.list_load_more()}
                        </Button>
                    )}
                </CardContent>
            </Card>
        </div>
    )
}

function Field({ label, value }: { label: string; value: string }) {
    return (
        <div>
            <p className="text-xs text-muted-foreground">{label}</p>
            <p className="text-base font-medium md:text-sm">{value}</p>
        </div>
    )
}
